import { z } from "zod";
import bedrock from "../../doctrine/pr17-living-self-model-bedrock-v1.json";
import { collectMirrorLanguageViolations } from "./mirror-bedrock";

export const LIVING_SELF_MODEL_BEDROCK = bedrock;
export const LIVING_SELF_MODEL_GOVERNING_INVARIANT = bedrock.governing_invariant;

export const LivingSelfModelAuthorityResponseSchema = z.enum([
  "yes",
  "partly",
  "no",
  "not_what_i_meant",
  "used_to_be_true",
  "missing_context",
]);

export type LivingSelfModelAuthorityResponse = z.infer<typeof LivingSelfModelAuthorityResponseSchema>;

export const LivingSelfModelAuthorityRequestSchema = z.object({
  response: LivingSelfModelAuthorityResponseSchema,
  text: z.string().trim().min(1).max(2000).optional(),
}).strict().superRefine((value, ctx) => {
  if (
    ["partly", "not_what_i_meant", "missing_context"].includes(value.response)
    && !value.text
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["text"],
      message: "This authority response requires your wording or missing context.",
    });
  }
});

export type LivingSelfModelAuthorityRequest = z.infer<typeof LivingSelfModelAuthorityRequestSchema>;

export const LivingSelfModelClaimKindSchema = z.enum([
  "active_will",
  "domain_summary",
  "working_hypothesis",
  "open_loop",
  "known_unknown",
]);

export const LivingSelfModelClaimSchema = z.object({
  id: z.string().trim().min(1),
  kind: LivingSelfModelClaimKindSchema,
  text: z.string().trim().min(1).max(2000),
  sourceIds: z.array(z.string().trim().min(1)).max(32),
  relatedIds: z.array(z.string().trim().min(1)).max(32).default([]),
  crossDomain: z.boolean().default(false),
  identityVerdict: z.literal(false),
  confidenceCustomerFacing: z.literal(false),
  authorityPrompt: z.string().trim().min(1).max(300),
}).superRefine((claim, ctx) => {
  const requiresSources = bedrock.claim_contract.source_trace_required_for.includes(claim.kind);
  if (requiresSources && claim.sourceIds.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["sourceIds"],
      message: "customer-visible model claim requires an evidence/source trail",
    });
  }
  if (claim.kind === "known_unknown" && claim.sourceIds.length === 0 && claim.relatedIds.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["relatedIds"],
      message: "known unknown must reference related model items when no direct source exists",
    });
  }
});

export type LivingSelfModelClaim = z.infer<typeof LivingSelfModelClaimSchema>;

export type LivingSelfModelClaimViolation =
  | { kind: "mirror_language"; value: string }
  | { kind: "identity_frame"; value: string }
  | { kind: "clinical_frame"; value: string }
  | { kind: "confidence_leak"; value: string }
  | { kind: "attention_score_leak"; value: string };

const IDENTITY_FRAME = [
  /\bthis is who you are\b/i,
  /\bthis proves who you are\b/i,
  /\byou are (?:an?|the) [a-z][a-z -]{2,40}\b/i,
];

const CLINICAL_FRAME = [
  /\bdiagnos(?:is|ed|tic)\b/i,
  /\bpersonality profile\b/i,
  /\bpathology\b/i,
  /\bmaladaptive\b/i,
  /\bcognitive distortion\b/i,
];

const SCORE_FRAME = [
  /\b\d+(?:\.\d+)?%\s*confidence\b/i,
  /\bconfidence\s*[:=]\s*\d/i,
];

const ATTENTION_FRAME = [
  /\battention\s*score\b/i,
];

export function collectLivingSelfModelClaimViolations(text: string): LivingSelfModelClaimViolation[] {
  const violations: LivingSelfModelClaimViolation[] = [];
  for (const item of collectMirrorLanguageViolations(text)) {
    violations.push({ kind: "mirror_language", value: item.value });
  }
  for (const pattern of IDENTITY_FRAME) {
    const match = text.match(pattern);
    if (match) violations.push({ kind: "identity_frame", value: match[0] });
  }
  for (const pattern of CLINICAL_FRAME) {
    const match = text.match(pattern);
    if (match) violations.push({ kind: "clinical_frame", value: match[0] });
  }
  for (const pattern of SCORE_FRAME) {
    const match = text.match(pattern);
    if (match) violations.push({ kind: "confidence_leak", value: match[0] });
  }
  for (const pattern of ATTENTION_FRAME) {
    const match = text.match(pattern);
    if (match) violations.push({ kind: "attention_score_leak", value: match[0] });
  }
  return violations;
}

export function livingSelfModelClaimAllowed(text: string): boolean {
  return collectLivingSelfModelClaimViolations(text).length === 0;
}

export function makeLivingSelfModelClaim(
  input: Omit<LivingSelfModelClaim, "identityVerdict" | "confidenceCustomerFacing">,
): LivingSelfModelClaim {
  if (!livingSelfModelClaimAllowed(input.text)) {
    throw new Error("pr17_claim_language_violation");
  }
  return LivingSelfModelClaimSchema.parse({
    ...input,
    identityVerdict: false,
    confidenceCustomerFacing: false,
  });
}

export const LIVING_SELF_MODEL_AUTHORITY_LABELS: Record<LivingSelfModelAuthorityResponse, string> = {
  yes: "Yes",
  partly: "Partly",
  no: "No",
  not_what_i_meant: "That's not what I meant",
  used_to_be_true: "That used to be true",
  missing_context: "You're missing something",
};
